import { EventTypes, useEditor } from '@candulabs/craft';
import invariant from 'invariant';
import throttle from 'lodash/throttle';
import React, { KeyboardEvent } from 'react';
import EventListener from 'react-event-listener';
import { useDispatch } from 'react-redux';

import { useEventing } from 'commons/eventing';
import { hotkeys } from 'commons/hotkeys';
import { CRAFT_ROOT_ID, TypographyVariants } from 'craft/constants';
import { InsertMode, useCopyCutAndPaste, useNodeDom, useTypographyNodeMarks } from 'craft/hooks';
import { useMutations } from 'craft/hooks/useMutations';
import { useSelection } from 'craft/hooks/useSelection';
import { constants as typographyStyles } from 'craft/resolvers/Typography';
import {
  createFocusTupleFromTargetElement,
  getRTEAncestorFromTargetElement,
} from 'craft/rte/utils';
import { setCollectionItemEvents } from 'craft/state';
import { CustomNodeData } from 'craft/types';

import { checkConditions } from './checkConditions';
import { KeyMap } from './types';
import { useFlowsShortcuts } from './useFlowsShortcuts';
import { useMenuShortcuts } from './useMenuShortcuts';
import { useNavigationShortcuts } from './useNavigationShortcuts';
import { useShortcutQueries } from './useShortcutQueries';

export const EventManager = React.memo(() => {
  const { actions, query } = useEditor();
  const { track } = useEventing();
  const mutations = useMutations();
  const { onCopy, onCut, onPaste } = useCopyCutAndPaste();
  const { selection, previousNodeId, nextSiblingId } = useSelection();
  const dispatch = useDispatch();
  const {
    getFocusNodeId,
    isInFocusMode,
    isInSelectionMode,
    isSelectedAndIsNotRoot,
    isDeletable,
    isTypographyNode,
    noSelection,
  } = useShortcutQueries();
  const hasFocus = isInFocusMode();
  const navigationShortcuts = useNavigationShortcuts();
  const flowShortcuts = useFlowsShortcuts();
  const menuShortcuts = useMenuShortcuts();
  const { toggleMark } = useTypographyNodeMarks();

  const keyMap: KeyMap = {
    /** removing nodes */
    deleteNode: {
      hotkeys: 'del',
      enabled: checkConditions(isInSelectionMode, isDeletable),
    },
    backspaceNode: {
      hotkeys: 'backspace',
      enabled: checkConditions(isInSelectionMode, isDeletable),
    },

    /** enter/escape focus */
    enterWithNoSelection: {
      hotkeys: 'enter',
      enabled: checkConditions(noSelection),
    },
    enterWithSelection: {
      hotkeys: 'enter',
      enabled: checkConditions(isInSelectionMode),
    },
    escapeWithSelection: {
      hotkeys: 'escape',
      enabled: checkConditions(isInSelectionMode),
    },
    escapeWithFocus: {
      hotkeys: 'escape',
      enabled: checkConditions(isInFocusMode),
    },

    tabWithFocus: {
      hotkeys: 'tab',
      enabled: checkConditions(isInFocusMode),
    },

    ...flowShortcuts.keyMap,
    ...menuShortcuts.keyMap,
    ...navigationShortcuts.keyMap,

    /** Typography */
    typography: {
      // size must be the last operand for the handler to work properly.
      hotkeys: [0, 1, 2, 3, 4, 5, 6].map((size) => `mod+opt+${size}`),
      enabled: checkConditions(
        () => (isInSelectionMode() || isInFocusMode()) && isTypographyNode(),
      ),
    },

    /** Marks */
    bold: {
      hotkeys: 'mod+b',
      enabled: checkConditions(isInSelectionMode),
    },
    underline: {
      hotkeys: 'mod+u',
      enabled: checkConditions(isInSelectionMode),
    },
    italics: {
      hotkeys: 'mod+i',
      enabled: checkConditions(isInSelectionMode),
    },

    /** Clipboard */
    copy: {
      hotkeys: 'mod+c',
      enabled: checkConditions(isSelectedAndIsNotRoot),
    },
    cut: {
      hotkeys: 'mod+x',
      enabled: checkConditions(isSelectedAndIsNotRoot),
    },
    pasteTree: {
      hotkeys: 'mod+v',
      enabled: checkConditions(isInSelectionMode),
    },
    pasteTreeInto: {
      hotkeys: 'mod+shift+v',
      enabled: checkConditions(isInSelectionMode),
    },
    pasteText: {
      hotkeys: 'mod+v',
      enabled: checkConditions(isInFocusMode),
    },

    /** History */
    undo: {
      hotkeys: 'mod+z',
      enabled: () => true,
    },
    redo: {
      hotkeys: 'mod+shift+z',
      enabled: () => true,
    },
  };

  const deleteNode = (nodeIds: string[], findSibling: (id: string) => string | null) => {
    if (nodeIds.length === 0) {
      return;
    }
    // Use last selected nodeId for finding a sibling Node
    const nodeId = nodeIds[nodeIds.length - 1];
    const node = query.node(nodeId).get();

    let foundSiblingNode;
    let currId: string | null = findSibling(nodeId);

    // Loop until found an EXISTING previous node
    while (currId) {
      if (!query.node(currId).get()) {
        currId = findSibling(currId);
      } else {
        foundSiblingNode = currId;
        break;
      }
    }
    actions.selectNode(foundSiblingNode || node?.data?.parent || CRAFT_ROOT_ID);

    mutations.deleteNode(nodeIds);
  };

  const handlers = {
    deleteNode: (e: KeyboardEvent) => {
      e.preventDefault();
      deleteNode(selection.all, nextSiblingId);
    },

    backspaceNode: (e: KeyboardEvent) => {
      e.preventDefault();
      deleteNode(selection.all, previousNodeId);
    },

    enterWithNoSelection: () => actions.selectNode(CRAFT_ROOT_ID),
    enterWithSelection: (e: KeyboardEvent) => {
      if (selection.isEmpty) {
        return;
      }

      const rteNodeAncestor = getRTEAncestorFromTargetElement(e.target as Element | null);

      const nodeName = query.node(selection.last).get()?.data?.name;
      const nodeId = getFocusNodeId(nodeName);

      if (!nodeId) {
        return;
      }

      if (rteNodeAncestor) {
        e.preventDefault();
        // we need to store which particular node received the click event, as this is where the user intents to see the caret
        const [focusedElement, targetedNode] = createFocusTupleFromTargetElement(
          e.target as Element | null,
        );
        actions.setNodeEvent(EventTypes.selected, [focusedElement.split('-')[0]]);
        actions.setNodeEvent(EventTypes.focused, [focusedElement, targetedNode.split('-')[0]]);
      }
    },

    escapeWithSelection: () => actions.selectNode(),
    escapeWithFocus: () => actions.setNodeEvent(EventTypes.focused, []),
    tabWithFocus: () => actions.setNodeEvent(EventTypes.focused, []),

    ...flowShortcuts.handlers,
    ...navigationShortcuts.handlers,
    ...menuShortcuts.handlers,

    /** Typography */
    typography: (e: KeyboardEvent, hotkey: string) => {
      if (selection.isEmpty) {
        return;
      }
      const shortcutIndex = parseInt(hotkey[hotkey.length - 1], 10);
      // sanity check...
      invariant(
        shortcutIndex >= 0 && shortcutIndex < 5,
        'The Typography Keyboard shortcut is not valid',
      );
      const variant = shortcutIndex === 0 ? TypographyVariants.P : `H${shortcutIndex}`;
      actions.setCustom(selection.last, (properties: CustomNodeData) => {
        // eslint-disable-next-line no-param-reassign
        properties.custom = {
          ...(properties.custom || {}),
          variant,
        };

        /**
         * Need to update the variant css styles here so the spacing menu in the
         * toolbox is able to pick them up correctly.
         * */
        // @ts-expect-error
        // eslint-disable-next-line no-param-reassign
        properties.css = typographyStyles[variant || TypographyVariants.P];
      });
    },

    /** Marks */
    bold: () => toggleMark('B'),
    underline: () => toggleMark('U'),
    italics: () => toggleMark('Em'),

    /* clipboard handlers */
    copy: () => {
      track('editor.click.copy', { category: 'shortcut' });
      onCopy();
    },

    cut: (e: KeyboardEvent) => {
      if (hasFocus) {
        return null;
      }
      track('editor.click.cut', { category: 'shortcut' });
      onCut();
      return handlers.backspaceNode(e);
    },

    pasteTree: () => {
      track('editor.click.paste', { category: 'shortcut' });
      onPaste();
    },
    pasteTreeInto: () => {
      track('editor.click.pasteInto', { category: 'shortcut' });
      onPaste(InsertMode.INTO);
    },

    undo: () => {
      track('editor.click.undo', { category: 'shortcut' });
      actions.undo();
    },
    redo: () => {
      track('editor.click.redo', { category: 'shortcut' });
      actions.redo();
    },

    preventDefault: (e: KeyboardEvent) => e.preventDefault(),
  };

  const onKeyDown = React.useCallback(throttle(hotkeys({ keyMap, handlers }), 5), [
    keyMap,
    handlers,
  ]);

  const dom: HTMLElement = useNodeDom(selection.first);

  React.useEffect(() => {
    if (dom && !hasFocus) {
      (dom as HTMLElement | null)?.focus();
      dispatch(setCollectionItemEvents(EventTypes.selected, dom?.dataset?.collectionItemId));
    } else if (!hasFocus) {
      (document?.activeElement as HTMLElement | null)?.blur();
    }
  }, [dom?.dataset?.nid, hasFocus]);

  return (
    <EventListener
      target="window"
      // @ts-expect-error
      onKeyDown={onKeyDown}
    />
  );
});

EventManager.displayName = 'EventManager';
